import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  ViewEncapsulation,
  afterNextRender,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { CdkListbox, CdkOption, ListboxValueChangeEvent } from '@angular/cdk/listbox';
import { MatTooltip } from '@angular/material/tooltip';
import { DateAdapter } from '@angular/material/core';
import {
  DateRange,
  DefaultMatCalendarRangeStrategy,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatCalendar,
} from '@angular/material/datepicker';
import moment from 'moment-timezone';
import { SbButtonComponent } from '../sb-button/sb-button.component';
import { SB_CALENDAR_ADAPTER_PROVIDERS, SbCalendarDateAdapter } from './sb-calendar-date-adapter';
import { nextRange } from './sb-calendar-selection';
import { dateParts } from './sb-date-time-format';
import { SbDateParts } from './sb-date-time-parse';
import { SbTimeSegments, noHourMessage } from './sb-date-time-segments';
import { coarsePointer } from './sb-date-time-overlay';
import { SbPickView, nowIn } from './sb-quick-picks';

export type SbPanelMode = 'date' | 'time' | 'datetime' | 'range';

const ARIA_NOUN: Record<SbPanelMode, string> = { date: 'date', time: 'time', datetime: 'date and time', range: 'dates' };

/** Keys that scroll the page when the control they reach has no use for them. */
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End']);

/** How long keys typed in a time column add up to one row's name ("3", "35"), as in a native select. */
const TYPEAHEAD_MS = 1000;

/** How far a wheel's line goes, for a wheel that counts in lines (Firefox's mouse wheel). */
const WHEEL_LINE_PX = 40;

/** Keys that neither type in a time column nor end what was typed there. */
const MODIFIER_KEYS = new Set(['Shift', 'Control', 'Alt', 'Meta', 'AltGraph', 'CapsLock']);

/** A pick's value split around its time, which sits in a box as wide as "12:59 PM". */
const TIME_IN_VALUE_RE = /^(.*?)(\d{1,2}:\d{2} [AP]M)(.*)$/;

const toDate = (parts: SbDateParts | null): Date | null => (parts ? new Date(parts.year, parts.month, parts.day) : null);

let nextPanelId = 0;

/**
 * The popover of the date, time, datetime and range fields: a column of quick picks on the left,
 * Material's calendar (one month, its own header), hour/minute/AM-PM columns (every minute) and a
 * footer. Where the picks cannot sit beside the calendar they wrap above it (`fitPanel`). It only
 * reports choices; the field that opened it turns them into its value and decides when to close.
 */
@Component({
  selector: 'app-sb-date-time-panel',
  templateUrl: './sb-date-time-panel.component.html',
  styleUrls: ['./sb-date-time-panel.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatCalendar, CdkListbox, CdkOption, MatTooltip, SbButtonComponent],
  providers: [
    ...SB_CALENDAR_ADAPTER_PROVIDERS,
    { provide: MAT_DATE_RANGE_SELECTION_STRATEGY, useClass: DefaultMatCalendarRangeStrategy },
  ],
  host: {
    class: 'sb-dt-panel',
    role: 'dialog',
    'aria-modal': 'false',
    '[id]': 'panelId()',
    '[attr.aria-label]': 'ariaLabel()',
    '[class]': 'modeClass()',
    '[class.sb-dt-panel--narrow]': 'narrow()',
    '[class.sb-dt-panel--capped]': 'capped()',
    '(keydown)': 'onKeydown($event)',
    '(wheel)': 'onWheel($event)',
  },
})
export class SbDateTimePanelComponent {
  readonly mode = input.required<SbPanelMode>();
  readonly panelId = input(`sb-dt-panel-${++nextPanelId}`);
  readonly label = input('');
  readonly selectedDate = input<SbDateParts | null>(null);
  readonly rangeStart = input<SbDateParts | null>(null);
  readonly rangeEnd = input<SbDateParts | null>(null);
  /** The field's hour, minute and AM/PM, each shown as chosen when the field holds it. */
  readonly time = input<SbTimeSegments | null>(null);
  /** Counts the field's edits and the page's writes, each of which drops a minute or AM/PM waiting for the hour. */
  readonly fieldEdits = input(0);
  readonly min = input<SbDateParts | null>(null);
  readonly max = input<SbDateParts | null>(null);
  readonly picks = input<readonly SbPickView[]>([]);
  readonly showClear = input(false);
  /** The footer's Today (date) or Now (time), shown only when the field has no quick picks. */
  readonly showToday = input(false);
  /** Below a 500px viewport the panel stacks, its picks as tiles above the calendar. */
  readonly narrow = input(false);

  readonly dayChosen = output<{ date: SbDateParts; viaKeyboard: boolean }>();
  readonly rangeChosen = output<{ start: SbDateParts; end: SbDateParts }>();
  /** The segments an hour, minute or AM/PM choice sets. */
  readonly timeChosen = output<Partial<SbTimeSegments>>();
  readonly pickChosen = output<SbPickView>();
  readonly cleared = output<void>();
  readonly todayChosen = output<void>();
  readonly done = output<void>();
  readonly escaped = output<void>();
  /** Tab past either end of the panel. */
  readonly tabbedOut = output<'forward' | 'back'>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly announcer = inject(LiveAnnouncer);
  private readonly adapter = inject(DateAdapter) as SbCalendarDateAdapter;
  /** The zone "today" and "now" are taken in; the calendar's adapter reads it for its today mark. */
  readonly zone = input<string | null, string | null>(null, {
    transform: (zone) => {
      this.adapter.zone = zone;
      return zone;
    },
  });
  private readonly calendar = viewChild(MatCalendar);
  private readonly pickButtons = viewChildren<ElementRef<HTMLButtonElement>>('pickButton');
  private readonly hourList = viewChild<ElementRef<HTMLElement>>('hourList');
  private readonly hourListbox = viewChild('hourList', { read: CdkListbox });
  private readonly hourOptions = viewChildren('hourOption', { read: CdkOption });
  private readonly columnEls = viewChildren<ElementRef<HTMLElement>>('column');
  private readonly columnLists = viewChildren('column', { read: CdkListbox });
  private readonly options = viewChildren(CdkOption);
  private lastKeyWasEnter = false;
  /** Where the picks list was scrolled to when the panel capped, for when it no longer is. */
  private picksTop = 0;
  /**
   * Keys typed in a time column, while they still add up to a row's name; `refused` once they name
   * none; `passed` what Enter says after keys the column passes over, which name none either.
   */
  private typed = { list: null as CdkListbox | null, keys: '', at: 0, refused: '', passed: '' };

  readonly activePick = signal(0);
  /** Capped to less height than it needs, so its body scrolls: the picks then scroll with it, not on their own. */
  readonly capped = signal(false);
  /** The status line: a disabled pick's reason once it is tapped or pressed. */
  readonly status = signal('');
  /** A range's first click, until the second. */
  readonly pending = signal<DateRange<Date> | null>(null);
  readonly hour12 = computed(() => this.time()?.hour ?? null);
  /**
   * A minute or AM/PM chosen while the field has no hour (none held, deleted, a digit waiting or
   * refused): it shows as chosen and waits for the hour, writing nothing. Anything edited in the
   * field, or written by the page, drops it, and the columns show the field's parts again.
   */
  readonly pendingMinute = linkedSignal<number, number | null>({ source: this.fieldEdits, computation: () => null });
  readonly pendingPeriod = linkedSignal<number, number | null>({ source: this.fieldEdits, computation: () => null });

  readonly modeClass = computed(() => `sb-dt-panel--${this.mode()}`);
  readonly ariaLabel = computed(() => `${this.label() ? `${this.label()}: ` : ''}choose ${ARIA_NOUN[this.mode()]}`);
  readonly hasCalendar = computed(() => this.mode() !== 'time');
  readonly hasTime = computed(() => this.mode() === 'time' || this.mode() === 'datetime');
  readonly minDate = computed(() => toDate(this.min()));
  readonly maxDate = computed(() => toDate(this.max()));

  readonly calendarSelected = computed<Date | DateRange<Date> | null>(() => {
    if (this.mode() !== 'range') {
      return toDate(this.selectedDate());
    }
    return this.pending() ?? new DateRange(toDate(this.rangeStart()), toDate(this.rangeEnd()));
  });

  /** A range opens on its end's month: filters mostly end now, and the start's month would hide that. */
  readonly startAt = computed(() => {
    const day = this.mode() === 'range' ? this.rangeEnd() ?? this.rangeStart() : this.selectedDate();
    return toDate(day) ?? this.adapter.today();
  });

  readonly picksName = computed(() => (this.mode() === 'range' ? 'Presets' : 'Quick picks'));

  /** Shown when "today" and "now" are not the browser's. */
  readonly zoneNote = computed(() => {
    const zone = this.zone();
    return zone && zone !== moment.tz.guess() ? `Today and now in ${zone}` : '';
  });

  readonly hours = Array.from({ length: 12 }, (_, i) => i + 1);
  readonly periods = ['AM', 'PM'] as const;
  /** Every minute: a typed or held minute always has its row. */
  readonly minutes = Array.from({ length: 60 }, (_, i) => i);
  /** The minute and AM/PM shown as chosen: one chosen since the field lost its hour, else the field's. */
  private readonly shownMinute = computed(() => this.pendingMinute() ?? this.time()?.minute ?? null);
  private readonly shownPeriod = computed(() => this.pendingPeriod() ?? this.time()?.period ?? null);
  readonly minuteValue = computed(() => {
    const minute = this.shownMinute();
    return minute === null ? [] : [minute];
  });
  readonly period = computed(() => {
    const period = this.shownPeriod();
    return period === null ? null : period ? 'PM' : 'AM';
  });

  constructor() {
    afterNextRender(() => {
      this.centreSelectedRows();
      const body = this.body();
      if (!body) {
        return;
      }
      this.measureCapped(body);
      // The line under the panel, a month with another week or a datetime's columns moving under
      // the calendar change what the body has to show, and the room it has, while the panel is open.
      // The calendar's own box too: capped, the picks' full length can hide a month a week shorter.
      if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(() => this.measureCapped(body));
        [body, ...Array.from(body.querySelectorAll('.sb-dt-panel__content, .sb-dt-panel__main'))].forEach((el) => observer.observe(el));
        this.destroyRef.onDestroy(() => observer.disconnect());
      }
    });
    // Ahead of the listbox's own typeahead, which waits 200ms for more keys: an Enter in that time
    // chose the row that was active before them.
    this.host.nativeElement.addEventListener('keydown', (event: KeyboardEvent) => this.onColumnTypeahead(event), true);
  }

  pad(value: number): string {
    return String(value).padStart(2, '0');
  }

  /**
   * Focus for a keyboard open: the calendar's active day, or the hour column for a time. A phone's
   * panel (a narrow one under a touch screen) opens at its top, so focus starts there, on the
   * picks, not on a day further down; a keyboard at high zoom still starts on the day.
   */
  focusInitial(): void {
    const pick = this.narrow() && coarsePointer() ? this.pickButtons()[this.activePick()] : undefined;
    if (pick) {
      pick.nativeElement.focus({ preventScroll: true });
      return;
    }
    if (this.hasCalendar()) {
      this.keepBodyTopOnFocus();
      this.calendar()?.focusActiveCell();
      return;
    }
    this.focusHourColumn();
  }

  /**
   * A capped panel opens at the top of its body, its first pick whole, when the day that takes focus
   * shows whole there; else the body scrolls no further than shows the whole day, which a focused
   * control never hides. The browser scrolls the body as the day is focused, its own way, and the
   * calendar focuses it a moment later, so the body is set from its top once that is done, wherever
   * a datetime's time columns had brought it.
   */
  private keepBodyTopOnFocus(): void {
    const body = this.body();
    if (!body) {
      return;
    }
    body.addEventListener('focusin', (event) => queueMicrotask(() => {
      // How far the day's bottom would reach past the body's visible part, with the body at its top.
      const day = (event.target as HTMLElement).getBoundingClientRect();
      const shown = body.getBoundingClientRect();
      const top = day.top + body.scrollTop - shown.top;
      body.scrollTop = Math.max(0, Math.min(day.bottom + body.scrollTop - shown.bottom, top));
    }), { once: true });
  }

  focusHourColumn(): void {
    const list = this.hourList()?.nativeElement;
    const nearest = this.hour12() === null ? this.hourOptions().find((option) => option.value === this.nowHour12()) : undefined;
    if (nearest) {
      // With no hour chosen the listbox would start on 1, scrolled to the top; start on the hour
      // nearest now, the row the column is centred on.
      this.hourListbox()?._setActiveOption(nearest);
      this.centreSelectedRows();
      return;
    }
    (list?.querySelector<HTMLElement>('[tabindex="0"]') ?? list)?.focus();
  }

  onCalendarChange(clicked: Date | null): void {
    if (!clicked) {
      return;
    }
    if (this.mode() !== 'range') {
      this.dayChosen.emit({ date: dateParts(clicked), viaKeyboard: this.lastKeyWasEnter });
      return;
    }
    const current = this.pending() ?? new DateRange<Date>(null, null);
    const { range, finished } = nextRange(clicked, current, this.adapter);
    if (finished && range.start && range.end) {
      this.pending.set(null);
      this.rangeChosen.emit({ start: dateParts(range.start), end: dateParts(range.end) });
      return;
    }
    this.pending.set(range);
  }

  /**
   * An hour makes a whole time with the minutes and AM/PM shown as chosen (ones waiting for the
   * hour, else the field's); the field fills what is still empty as any column choice does.
   */
  onHour(event: ListboxValueChangeEvent<number>): void {
    const hour = event.value[0];
    if (hour === undefined) {
      return;
    }
    this.endTyping();
    this.timeChosen.emit({ hour, minute: this.shownMinute(), period: this.shownPeriod() });
    this.pendingMinute.set(null);
    this.pendingPeriod.set(null);
  }

  /** A minute sets only the minutes; with no hour yet it waits for the hour, so nothing is written that nobody chose. */
  onMinute(event: ListboxValueChangeEvent<number>): void {
    const minute = event.value[0];
    if (minute === undefined) {
      return;
    }
    this.endTyping();
    if (this.hour12() === null) {
      this.pendingMinute.set(minute);
      return;
    }
    this.timeChosen.emit({ minute });
  }

  onPeriod(event: ListboxValueChangeEvent<string>): void {
    const period = event.value[0] === 'PM' ? 1 : event.value[0] === 'AM' ? 0 : null;
    if (period === null) {
      return;
    }
    this.endTyping();
    if (this.hour12() === null) {
      this.pendingPeriod.set(period);
      return;
    }
    this.timeChosen.emit({ period });
  }

  /** A day chosen in a stacked date and time panel brings its time columns, further down, into view. */
  revealTime(): void {
    const body = this.body();
    const time = this.host.nativeElement.querySelector<HTMLElement>('.sb-dt-time');
    if (!body || !time || !this.narrow()) {
      return;
    }
    const shown = body.getBoundingClientRect();
    const box = time.getBoundingClientRect();
    const below = box.bottom - shown.bottom;
    if (below > 0) {
      body.scrollTop += Math.min(below, box.top - shown.top);
    }
  }

  /**
   * A disabled pick does nothing but say why, in the status line, so touch users can read it too.
   * The line takes room from the body: the pick stays in view.
   */
  choosePick(view: SbPickView): void {
    if (view.reason || !view.result) {
      this.status.set(`${view.label}: unavailable, ${view.reason}`);
      this.announcer.announce(this.status(), 'polite');
      afterNextRender(() => this.pickButtons()[this.picks().indexOf(view)]?.nativeElement.scrollIntoView({ block: 'nearest' }), { injector: this.injector });
      return;
    }
    this.status.set('');
    this.pickChosen.emit(view);
  }

  /** A pick's value around its time: `["Wed, Oct 28, ", "6:34 PM", ""]`; no time, `[value, "", ""]`. */
  pickParts(value: string): [string, string, string] {
    const match = value.match(TIME_IN_VALUE_RE);
    return match ? [match[1], match[2], match[3]] : [value, '', ''];
  }

  /** Roving tabindex across the picks: one tab stop, the arrows in reading order and Home/End move. */
  onPickKeydown(event: KeyboardEvent, index: number): void {
    const count = this.picks().length;
    const moves: Record<string, number> = { ArrowDown: index + 1, ArrowRight: index + 1, ArrowUp: index - 1, ArrowLeft: index - 1, Home: 0, End: count - 1 };
    if (!(event.key in moves)) {
      return;
    }
    event.preventDefault();
    const next = Math.max(0, Math.min(count - 1, moves[event.key]));
    this.activePick.set(next);
    this.status.set('');
    this.pickButtons()[next]?.nativeElement.focus();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.isComposing || event.keyCode === 229) {
      return;
    }
    this.lastKeyWasEnter = event.key === 'Enter' || event.key === ' ';
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.pending.set(null);
      this.escaped.emit();
      return;
    }
    // An arrow on a button (the calendar header, the footer) would scroll the page under the
    // panel, which closes it with focus lost; the calendar and the columns have used theirs.
    if (!event.defaultPrevented && SCROLL_KEYS.has(event.key)) {
      event.preventDefault();
    }
  }

  /** A press in the panel (a day, a pick, a row) ends what was typed in a time column, and its refusal. */
  onPointerDown(): void {
    this.lastKeyWasEnter = false;
    this.endTyping();
  }

  /**
   * A wheel over a capped panel outside its body (the line under it, the footer, the padding)
   * scrolls the body: it would scroll the page, which closes the panel. A pinch or Ctrl+wheel zooms.
   */
  onWheel(event: WheelEvent): void {
    const body = this.body();
    if (!this.capped() || !body || body.contains(event.target as Node) || event.ctrlKey) {
      return;
    }
    event.preventDefault();
    const unit = { [WheelEvent.DOM_DELTA_LINE]: WHEEL_LINE_PX, [WheelEvent.DOM_DELTA_PAGE]: body.clientHeight }[event.deltaMode] ?? 1;
    body.scrollTop += event.deltaY * unit;
  }

  /** Focus sentinels: Tab past the end closes the panel and moves on; Shift+Tab before the start goes back. */
  onSentinelFocus(direction: 'forward' | 'back'): void {
    this.tabbedOut.emit(direction);
  }

  todayLabel(): string {
    return this.mode() === 'time' ? 'Now' : 'Today';
  }

  /** The disabled reason for the footer's Today / Now, or ''. */
  todayReason(): string {
    if (this.mode() === 'time') {
      return '';
    }
    const today = nowIn(this.zone()).date;
    const min = this.min();
    const max = this.max();
    const key = (d: SbDateParts) => d.year * 10000 + d.month * 100 + d.day;
    if (min && key(today) < key(min)) {
      return 'unavailable: before the earliest allowed date';
    }
    return max && key(today) > key(max) ? 'unavailable: after the latest allowed date' : '';
  }

  /**
   * A digit or letter typed in a time column moves to the first row whose name starts with the keys
   * typed so far, as in a native list, at once, so Enter right after it chooses that row: "3" goes
   * to 30 in the minutes, "35" to 35, "05" to 05; "07" to the hour 7, as the field reads it. A digit
   * no minute starts with, 6-9, is that minute: "7" goes to 07. Digits that add up to no row ("13" in
   * the hours, "65" in the minutes), and in the AM/PM column any key but A or P, choose nothing: the
   * row stays where the keys before them put it, the status line says why in the field's words, and
   * Enter or Space chooses nothing, the line still saying why, until a key typed after a pause starts
   * again, another key (an arrow, Backspace) or a press in the panel ends it. Keys typed within the
   * pause say it again, but for a letter that names a row: A or P after a stray key is AM or PM. A
   * pause alone does not end it: an Enter after it would choose the row the first digit went to,
   * which nobody meant. A letter that adds up to no row starts again on its own, as P after A does.
   * A lone 0 in the hours, waiting for the hour it starts, and a letter in the hours or the minutes
   * move nothing and say nothing, but name no row: Enter or Space after them chooses nothing either,
   * and says why, or it would choose the row the column happened to start on.
   */
  private onColumnTypeahead(event: KeyboardEvent): void {
    const column = (event.target as HTMLElement).closest<HTMLElement>('.sb-dt-time__column');
    const list = this.columnLists()[this.columnEls().findIndex((el) => el.nativeElement === column)];
    if (!column || !list || event.isComposing) {
      return;
    }
    const typing = event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey;
    if (!typing) {
      const why = this.typed.refused || this.typed.passed;
      if (why && this.typed.list === list && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        event.stopPropagation();
        this.typed = { ...this.typed, refused: why };
        this.status.set(why);
        this.announcer.announce(why, 'polite');
      } else if (!MODIFIER_KEYS.has(event.key)) {
        this.endTyping();
      }
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const key = event.key.toUpperCase();
    const now = Date.now();
    const continuing = this.typed.list === list && now - this.typed.at < TYPEAHEAD_MS;
    const rows = this.options().filter((option) => column.contains(option.element));
    const named = (prefix: string) => rows.find((option) => option.getLabel().toUpperCase().startsWith(prefix));
    const starting = (prefix: string) =>
      named(prefix) ?? (/^\d$/.test(prefix) ? named(`0${prefix}`) : /^0\d$/.test(prefix) ? named(prefix.slice(1)) : undefined);
    // As in the field: keys after a refusal say it again rather than add to it, all but a letter
    // that names a row, which starts again on its own (A or P after a stray key in the AM/PM column).
    if (continuing && this.typed.refused && !(/\D/.test(key) && starting(key))) {
      this.typed = { ...this.typed, at: now };
      this.announcer.announce(this.typed.refused, 'polite');
      return;
    }
    const keys = continuing ? this.typed.keys + key : key;
    const entry = starting(keys) || /^\d+$/.test(keys) ? keys : key;
    const row = starting(entry);
    const why = row ? '' : this.refusal(list, entry);
    if (why) {
      this.typed = { list, keys: entry, at: now, refused: why, passed: '' };
      this.status.set(why);
      this.announcer.announce(why, 'polite');
      return;
    }
    this.endTyping();
    this.typed = { list, keys: entry, at: now, refused: '', passed: row ? '' : this.passedOver(list, entry) };
    if (row) {
      list._setActiveOption(row);
    }
  }

  /**
   * Why keys that name no row in a time column are refused, in the field's words; '' for keys the
   * column passes over: a lone 0 waiting for the hour it starts ("07"), a letter in the hours or the
   * minutes, the M of "PM" once AM or PM is chosen, as the field takes it.
   */
  private refusal(list: CdkListbox, entry: string): string {
    const [hours, minutes] = this.columnLists();
    if (list === hours || list === minutes) {
      return entry.length < 2 || !/^\d+$/.test(entry) ? '' : list === hours ? noHourMessage(entry) : `There is no minute ${entry}`;
    }
    return entry === 'M' && this.period() !== null ? '' : 'Type A or P';
  }

  /** What Enter says after keys the hours or the minutes pass over. */
  private passedOver(list: CdkListbox, entry: string): string {
    const [hours, minutes] = this.columnLists();
    return list !== hours && list !== minutes ? '' : entry === '0' ? noHourMessage(entry) : 'Type digits only';
  }

  /** Ends a run of keys typed in a time column, and the refusal it said, if any. */
  private endTyping(): void {
    if (this.typed.refused && this.status() === this.typed.refused) {
      this.status.set('');
    }
    this.typed = { list: null, keys: '', at: 0, refused: '', passed: '' };
  }

  /** The part of the panel that scrolls when it is capped: the picks, the calendar and the time columns. */
  private body(): HTMLElement | null {
    return this.host.nativeElement.querySelector<HTMLElement>('.sb-dt-panel__body');
  }

  /**
   * Capped when the body cannot show what it holds with the picks held to the calendar's height, by
   * more than a pixel (a cap that rounds a fraction away hides nothing worth a scroller). Measured
   * in that layout, not the capped one, in which the picks run their full length and would keep a
   * panel that has the room again capped; the body keeps where it was scrolled to, and the picks
   * list, which scrolls only uncapped, goes back to where it was. What has focus stays in view.
   */
  private measureCapped(body: HTMLElement): void {
    const panel = this.host.nativeElement;
    const list = panel.querySelector('.sb-dt-picks__list');
    const shown = panel.classList.contains('sb-dt-panel--capped');
    const top = body.scrollTop;
    const listTop = list?.scrollTop ?? 0;
    panel.classList.remove('sb-dt-panel--capped');
    const capped = body.scrollHeight - body.clientHeight > 1;
    this.capped.set(capped);
    panel.classList.toggle('sb-dt-panel--capped', capped);
    body.scrollTop = top;
    if (capped && !shown) {
      this.picksTop = listTop;
    } else if (shown && !capped && list) {
      list.scrollTop = this.picksTop;
    }
    if (shown !== capped && panel.contains(document.activeElement)) {
      document.activeElement?.scrollIntoView({ block: 'nearest' });
    }
  }

  private nowHour12(): number {
    return nowIn(this.zone()).time.hour % 12 || 12;
  }

  /**
   * Each time column scrolls its selected row, or now's hour and exact minute, to the middle of the part of
   * it that shows. A panel capped above a sticky save bar scrolls its body: the columns first give up
   * rows, down to three, so they fit, and if the body still hides them it scrolls them in. A phone's
   * panel opens at its top, picks first: the columns, further down, centre their rows in themselves.
   */
  private centreSelectedRows(): void {
    const body = this.body();
    const columns = Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>('.sb-dt-time__column'));
    const rowHeight = columns[0]?.querySelector<HTMLElement>('[role="option"]')?.offsetHeight ?? 0;
    if (!body || !rowHeight) {
      return;
    }
    const overflow = body.scrollHeight - body.clientHeight;
    if (overflow > 0) {
      columns.forEach((column) => (column.style.maxHeight = `${Math.max(rowHeight * 3, column.clientHeight - overflow)}px`));
      const hidden = columns[0].getBoundingClientRect().top + columns[0].clientHeight - body.getBoundingClientRect().bottom;
      if (hidden > 0 && !this.narrow()) {
        body.scrollTop += hidden;
      }
    }
    const now = nowIn(this.zone()).time;
    const fallback = [String(this.nowHour12()), this.pad(now.minute)];
    const shown = this.narrow() ? null : body.getBoundingClientRect();
    columns.forEach((column, index) => {
      const row = column.querySelector<HTMLElement>('[aria-selected="true"]')
        ?? (fallback[index] ? Array.from(column.querySelectorAll<HTMLElement>('[role="option"]')).find((option) => option.textContent?.trim() === fallback[index]) : null);
      if (!row) {
        return;
      }
      const box = column.getBoundingClientRect();
      const top = shown ? Math.max(box.top, shown.top) - box.top : 0;
      const bottom = shown ? Math.min(box.top + column.clientHeight, shown.bottom) - box.top : column.clientHeight;
      const rowTop = row.getBoundingClientRect().top - box.top + column.scrollTop;
      column.scrollTop = rowTop - top - (Math.max(bottom - top, rowHeight) - rowHeight) / 2;
    });
  }
}
